import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { apiErrorMessage, getAmendmentTermCatalogue, stageManualFacilityAmendment } from '../api/abfApi';
import type { FacilityContractVersion, ManualAmendmentRequest } from '../types/contractWorkspace';
import type { TermCatalogueEntry } from '../types/amendmentReview';
import { businessLabel, displayContractValue, readJsonValue } from '../presentation/contractPresentation';
import { Button, Card, CardHead, FieldLabel, INK, Input } from '../onboarding/review/reviewStyles';
import { Note } from '../onboarding/review/contractReviewStyles';
import ContractSchedulesEditor, {
  contractSchedulesFromVersion, contractSchedulesPayload, emptyContractSchedules, validateContractSchedules, type ContractSchedulesDraft,
} from '../components/ContractSchedulesEditor';
import CollectionsSchedulesEditor, {
  accountKeys as draftAccountKeys, collectionsSchedulesFromVersion, collectionsSchedulesPayload, emptyCollectionsSchedules, validateCollectionsSchedules, type CollectionsSchedulesDraft,
} from '../components/CollectionsSchedulesEditor';
import { formatAmountWithCommas, parseAmountShorthand, replaceShorthandOnBlur } from '../../../utils/amountUtils';
import { DateField } from '../../../components/shared/DateField';

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const Select = styled.select`
  box-sizing: border-box; width: 100%; padding: 0.5rem 0.6rem; border: 1px solid ${INK.lineStrong}; border-radius: 8px;
  background: ${INK.well}; color: ${INK.body}; color-scheme: dark; font: inherit; font-size: 0.76rem;
  option { background: #0f172a; }
`;

const TextArea = styled.textarea`
  box-sizing: border-box; width: 100%; min-height: 4rem; padding: 0.5rem 0.6rem; border: 1px solid ${INK.lineStrong}; border-radius: 8px;
  background: ${INK.well}; color: ${INK.body}; font: inherit; font-size: 0.76rem; resize: vertical;
`;

const TermRow = styled.div<{ $edited: boolean }>`
  display: grid; grid-template-columns: minmax(10rem, 0.8fr) minmax(8rem, 1fr) minmax(8rem, 1fr); gap: 10px; align-items: start;
  padding: 8px 10px; border: 1px solid ${p => p.$edited ? INK.accent + '88' : INK.line}; border-radius: 8px; margin-top: 6px;
  background: ${p => p.$edited ? '#12273c' : INK.raised};
  .name { color: ${INK.strong}; font-weight: 650; font-size: 13px; }
  .current { color: ${INK.muted}; font-size: 12px; margin-top: 2px; overflow-wrap: anywhere; }
  .clause { color: ${INK.faint}; font-size: 11px; margin-top: 2px; overflow-wrap: anywhere; }
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const GroupTitle = styled.h3`
  margin: 14px 0 2px; color: ${INK.accentSoft}; font-size: 12px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase;
`;

const GROUP_LABEL: Record<string, string> = {
  TERMS: 'Names and structure', BOOK_OF_RECORD: 'Pricing and commitment', ELIGIBILITY: 'Advance rate and reserve', CALENDAR: 'Dates',
};
const MONEY_KEYS = new Set(['totalCommitment', 'drawnAmount', 'reserveAmount']);
const SKIP_KEYS = new Set(['effectiveDate', 'portfolioId', 'facilityCode', 'externalId']);

interface Props {
  facilityId: number;
  active: FacilityContractVersion;
  onStaged: (version: FacilityContractVersion) => void;
  onCancel: () => void;
  onFeedback: (feedback: { tone: 'success' | 'error' | 'info'; message: string }) => void;
}

/** Slice 6 FR-6.2 / FR-6.9 — the structured lane: every term and schedule, prefilled from the active version. */
const StructuredAmendmentForm: React.FC<Props> = ({ facilityId, active, onStaged, onCancel, onFeedback }) => {
  const [catalogue, setCatalogue] = useState<TermCatalogueEntry[]>([]);
  const [header, setHeader] = useState({ sourceType: 'SIGNED_LETTER', authorityReference: '', effectiveFrom: '', rationale: '', provenance: '', sourceHash: '' });
  const [termValues, setTermValues] = useState<Record<string, string>>({});
  const [termSources, setTermSources] = useState<Record<string, string>>({});
  const [schedules, setSchedules] = useState<ContractSchedulesDraft>(emptyContractSchedules);
  const [schedulesTouched, setSchedulesTouched] = useState(false);
  const [collections, setCollections] = useState<CollectionsSchedulesDraft>(emptyCollectionsSchedules);
  const [collectionsTouched, setCollectionsTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    getAmendmentTermCatalogue().then(setCatalogue).catch(failure => onFeedback({ tone: 'error', message: apiErrorMessage(failure, 'The term catalogue could not be loaded.') }));
  }, [onFeedback]);

  const activeTerms = useMemo(() => new Map(active.terms.map(term => [term.term_key, term])), [active.terms]);
  const currentValue = (key: string): unknown => readJsonValue(activeTerms.get(key)?.verified_value_json);
  const isMoney = (key: string) => MONEY_KEYS.has(key);

  const grouped = useMemo(() => {
    const groups = new Map<string, TermCatalogueEntry[]>();
    catalogue.filter(entry => !SKIP_KEYS.has(entry.key)).forEach(entry => groups.set(entry.group, [...(groups.get(entry.group) || []), entry]));
    return Array.from(groups.entries());
  }, [catalogue]);

  const editedKeys = Object.keys(termValues).filter(key => {
    const typed = termValues[key];
    if (typed === undefined || typed.trim() === '') return false;
    const current = currentValue(key);
    const normalisedTyped = isMoney(key) ? String(parseAmountShorthand(typed)) : typed.trim();
    return String(current ?? '') !== normalisedTyped && displayContractValue(current) !== typed.trim();
  });

  // F-S6-020: payees and triggers are picked from what the version holds, not typed.
  const inForceAccountKeys = useMemo(() => (
    active.contractAccounts?.length ? active.contractAccounts : active.schedulesInForce?.accounts || []
  ).map(account => account.account_key), [active.contractAccounts, active.schedulesInForce]);
  const payeeAccountKeys = useMemo(() => collectionsTouched
    ? Object.values(draftAccountKeys(collections.accounts))
    : inForceAccountKeys, [collectionsTouched, collections.accounts, inForceAccountKeys]);
  const covenantOptions = useMemo(() => (active.covenants || []).map(covenant => ({ key: covenant.covenant_key, name: covenant.covenant_name })), [active.covenants]);

  const prefillContract = () => { setSchedules(contractSchedulesFromVersion(active)); setSchedulesTouched(true); };
  // Walkthrough fix (after F-S6-021): the prefilled accounts are a picture of what is in force, keyed by
  // type in the draft. If the maker leaves them untouched they are sent as "not stated" so the
  // server carries the in-force rows with their own keys and every column (currency, sweep
  // frequency, basis) — a calendar-only amendment must not restate four accounts under new keys.
  const [prefilledAccounts, setPrefilledAccounts] = useState<string | null>(null);
  const prefillCollections = () => {
    const draft = collectionsSchedulesFromVersion(active);
    setCollections(draft);
    setPrefilledAccounts(JSON.stringify(draft.accounts.map(({ id, ...rest }) => rest)));
    setCollectionsTouched(true);
  };
  const accountsUnchanged = prefilledAccounts !== null
    && JSON.stringify(collections.accounts.map(({ id, ...rest }) => rest)) === prefilledAccounts;

  const submit = async () => {
    if (busy) return;
    if (!header.authorityReference.trim() || !header.provenance.trim() || !header.effectiveFrom.trim()) {
      setProblem('The authority reference, effective date and provenance are required.'); return;
    }
    for (const key of editedKeys) {
      if (!(termSources[key] || '').trim()) { setProblem(`${businessLabel(key)} changed: enter the amendment section or clause that states the new value.`); return; }
    }
    const contractProblem = schedulesTouched ? validateContractSchedules(schedules) : null;
    if (contractProblem) { setProblem(`${contractProblem}.`); return; }
    const collectionsProblem = collectionsTouched ? validateCollectionsSchedules(collections) : null;
    if (collectionsProblem) { setProblem(`${collectionsProblem}.`); return; }
    setProblem(null);
    const values: Record<string, unknown> = {};
    const sources: Record<string, string> = {};
    editedKeys.forEach(key => {
      const entry = catalogue.find(item => item.key === key);
      const typed = termValues[key].trim();
      values[key] = isMoney(key) ? parseAmountShorthand(typed)
        : entry?.valueType === 'NUMBER' || entry?.valueType === 'INTEGER' ? Number(typed.replace(/,/g, '')) : typed;
      sources[key] = termSources[key].trim();
    });
    const payload: ManualAmendmentRequest = {
      ...header,
      sourceHash: header.sourceHash.trim() || undefined,
      ...(schedulesTouched ? contractSchedulesPayload(schedules) : { waterfallNodes: [], lenderClasses: [], consequences: [] }),
      ...(collectionsTouched
        ? { ...collectionsSchedulesPayload(collections), ...(accountsUnchanged ? { accounts: null } : {}) }
        : { accounts: null, paymentCalendar: null }),
      termValues: values,
      termSources: sources,
    };
    setBusy(true);
    try {
      const version = await stageManualFacilityAmendment(facilityId, payload);
      onFeedback({ tone: 'success', message: `Amendment version ${version.version_no} staged. Its review is open.` });
      onStaged(version);
    } catch (failure) {
      const message = apiErrorMessage(failure, 'The amendment could not be staged.');
      setProblem(message);
      onFeedback({ tone: 'error', message });
    } finally { setBusy(false); }
  };

  return <Card aria-label="Enter the amendment">
    <CardHead><div><h2>Enter the amendment</h2><p>Everything starts from active version {active.version_no}. Change only what the amendment changes; an unedited term or schedule is carried forward with its clause.</p></div></CardHead>
    <Grid>
      <FieldLabel>Source type<Select value={header.sourceType} onChange={event => setHeader({ ...header, sourceType: event.target.value })}>
        <option value="SIGNED_LETTER">Signed amendment letter</option><option value="CONSENT_RECORD">Consent record</option><option value="EMAIL">Email</option><option value="PORTAL_NOTICE">Portal notice</option><option value="OTHER">Other</option>
      </Select></FieldLabel>
      <FieldLabel>Effective date<DateField value={header.effectiveFrom} onChange={next => setHeader({ ...header, effectiveFrom: next })} /></FieldLabel>
      <FieldLabel>Authority reference<Input value={header.authorityReference} placeholder="e.g. Amendment No. 1 dated 15 Nov 2026" onChange={event => setHeader({ ...header, authorityReference: event.target.value })} /></FieldLabel>
      <FieldLabel>Source hash or external id (optional)<Input value={header.sourceHash} onChange={event => setHeader({ ...header, sourceHash: event.target.value })} /></FieldLabel>
    </Grid>
    <FieldLabel style={{ marginTop: 10 }}>Provenance<Input value={header.provenance} placeholder="Who supplied it and where the signed copy is retained" onChange={event => setHeader({ ...header, provenance: event.target.value })} /></FieldLabel>
    <FieldLabel style={{ marginTop: 10 }}>Rationale<TextArea value={header.rationale} placeholder="Why the parties amended, in a sentence or two" onChange={event => setHeader({ ...header, rationale: event.target.value })} /></FieldLabel>

    <h2 style={{ margin: '18px 0 0', fontSize: 15 }}>Terms</h2>
    <p style={{ color: INK.muted, fontSize: 13, margin: '4px 0 0' }}>{editedKeys.length === 0 ? 'No term changed yet.' : `${editedKeys.length} term${editedKeys.length === 1 ? '' : 's'} changed; each needs the section or clause that states the new value.`}</p>
    {grouped.map(([group, entries]) => <div key={group}>
      <GroupTitle>{GROUP_LABEL[group] || group}</GroupTitle>
      {entries.map(entry => {
        const edited = editedKeys.includes(entry.key);
        const current = currentValue(entry.key);
        const term = activeTerms.get(entry.key);
        return <TermRow key={entry.key} $edited={edited}>
          <div>
            <div className="name">{businessLabel(entry.key)}{entry.material && <span style={{ color: '#edcc82', fontSize: 11, marginLeft: 6 }}>material</span>}</div>
            <div className="current">Now: {isMoney(entry.key) && typeof current === 'number' ? formatAmountWithCommas(current) : displayContractValue(current, term?.value_presence)}</div>
            {term?.source_clause && <div className="clause">{term.source_clause}</div>}
          </div>
          <FieldLabel>New value
            <Input type={entry.valueType === 'DATE' ? 'date' : 'text'} inputMode={entry.valueType === 'TEXT' ? 'text' : 'decimal'}
              placeholder={isMoney(entry.key) ? 'e.g. 50MM' : 'Leave blank to keep'}
              value={termValues[entry.key] ?? ''}
              onChange={event => setTermValues({ ...termValues, [entry.key]: event.target.value })}
              onBlur={event => { if (isMoney(entry.key)) setTermValues({ ...termValues, [entry.key]: replaceShorthandOnBlur(event.target.value) }); }} />
          </FieldLabel>
          {edited ? <FieldLabel>Source reference (required)
            <Input value={termSources[entry.key] ?? ''} placeholder="e.g. Amendment No. 1 §2(a)" onChange={event => setTermSources({ ...termSources, [entry.key]: event.target.value })} />
          </FieldLabel> : <div className="current" style={{ alignSelf: 'center' }}>carried unchanged</div>}
        </TermRow>;
      })}
    </div>)}

    <h2 style={{ margin: '18px 0 0', fontSize: 15 }}>Waterfall, lender classes and consequences</h2>
    <p style={{ color: INK.muted, fontSize: 13, margin: '4px 0 8px' }}>{schedulesTouched ? 'A stated schedule replaces the active one whole.' : 'Not stated: the active version’s waterfall, classes and consequences are carried.'}</p>
    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
      <Button type="button" onClick={prefillContract}><RotateCcw size={13} /> Start from the active version</Button>
      {schedulesTouched && <Button $variant="quiet" type="button" onClick={() => { setSchedules(emptyContractSchedules()); setSchedulesTouched(false); }}>Carry unchanged instead</Button>}
    </div>
    {schedulesTouched && <ContractSchedulesEditor value={schedules} onChange={value => { setSchedules(value); setSchedulesTouched(true); }} accountKeys={payeeAccountKeys} covenants={covenantOptions} />}

    <h2 style={{ margin: '18px 0 0', fontSize: 15 }}>Accounts and payment calendar</h2>
    <p style={{ color: INK.muted, fontSize: 13, margin: '4px 0 8px' }}>{collectionsTouched ? 'A calendar change regenerates only future open periods; closed and evidenced periods keep their dates.' : 'Not stated: the active accounts and calendar are carried.'}</p>
    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
      <Button type="button" onClick={prefillCollections}><RotateCcw size={13} /> Start from the active version</Button>
      {collectionsTouched && <Button $variant="quiet" type="button" onClick={() => { setCollections(emptyCollectionsSchedules()); setCollectionsTouched(false); setPrefilledAccounts(null); }}>Carry unchanged instead</Button>}
    </div>
    {collectionsTouched && <CollectionsSchedulesEditor value={collections} onChange={value => { setCollections(value); setCollectionsTouched(true); }} effectiveFrom={header.effectiveFrom} authorityHint={header.authorityReference.trim() || undefined} idPrefix="amendment-collections" lane="AMENDMENT" />}

    {problem && <Note role="alert" style={{ marginTop: 12 }}>{problem}</Note>}
    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
      <Button $variant="primary" type="button" disabled={busy} onClick={() => void submit()}><CheckCircle2 size={14} /> {busy ? 'Staging…' : 'Stage and open the review'}</Button>
      <Button $variant="quiet" type="button" onClick={onCancel}>Cancel</Button>
    </div>
  </Card>;
};

export default StructuredAmendmentForm;
